import type { SampleRuleConditionGroup, SampleRuleConditions } from "@unbox/api";
import { AlertTriangle, ArrowLeft, HelpCircle, Plus } from "@untitledui/icons";
import { useEffect, useMemo, useState } from "react";
import { useWatch } from "react-hook-form";
import { useCreatorCohorts } from "@/api/creator-cohorts";
import { useCreateSampleRule, useSampleRules, useUpdateSampleRule } from "@/api/sample-rules";
import { SlideoutMenu } from "@/components/application/slideout-menus/slideout-menu";
import { Button } from "@/components/base/buttons/button";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { Input } from "@/components/base/input/input";
import { Toggle } from "@/components/base/toggle/toggle";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { useBulkActionContext } from "@/providers/bulk-action-progress-provider";
import { cx } from "@/utils/cx";
import { FIELD_OPTIONS } from "../condition-fields";
import { makeConditionGroup, toCreateRulePayload, toUpdateRulePayload, useRuleDraft } from "../use-rule-draft";
import { ConditionGroupCard } from "./condition-group-card";
import { ExemptCreatorList } from "./exempt-creator-list";
import { ImpactPreview } from "./impact-preview";
import { SampleRuleActionSelect } from "./sample-rule-action-select";

interface RuleBuilderDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ruleId?: string;
  onBack?: () => void;
  overlayClassName?: string;
  onCreated?: () => void;
}

export function RuleBuilderDrawer({
  open,
  onOpenChange,
  ruleId,
  onBack,
  overlayClassName,
  onCreated,
}: RuleBuilderDrawerProps) {
  const isEditing = !!ruleId;
  const { data: rulesData } = useSampleRules();
  const { data: cohortsData } = useCreatorCohorts();
  const createRule = useCreateSampleRule();
  const updateRule = useUpdateSampleRule();
  const { startTracking } = useBulkActionContext();
  const { form, conditions, exceptGroup, setExceptGroup, hydrate, canSave } = useRuleDraft();

  const cohortOptions = useMemo(() => (cohortsData ?? []).map((c) => ({ id: c.id, label: c.name })), [cohortsData]);

  const name = useWatch({ control: form.control, name: "name" });
  const action = useWatch({ control: form.control, name: "action" });
  const isActive = useWatch({ control: form.control, name: "isActive" });
  const reprocessPendingRequests = useWatch({ control: form.control, name: "reprocessPendingRequests" });

  const [hydratedKey, setHydratedKey] = useState<string | undefined>(undefined);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  useEffect(() => {
    if (!open) setShowDiscardConfirm(false);
  }, [open]);

  // Hydrates once per (open, ruleId) pair, waiting for `rulesData` to contain the row
  // when editing. Resetting `hydratedKey` on close guarantees the next open — whether
  // the same rule, a different rule, or a fresh "New Rule" — hydrates from scratch.
  useEffect(() => {
    if (!open) {
      setHydratedKey(undefined);
      return;
    }
    const key = ruleId ?? "__new__";
    if (hydratedKey === key) return;
    if (!ruleId) {
      hydrate(undefined);
      setHydratedKey(key);
      return;
    }
    const rule = rulesData?.items.find((r) => r.ruleId === ruleId);
    if (!rule) return;
    hydrate({
      name: rule.name,
      action: rule.action,
      conditions: rule.conditions as SampleRuleConditions,
      isActive: rule.isActive,
    });
    setHydratedKey(key);
  }, [open, ruleId, rulesData, hydratedKey, hydrate]);

  const whenGroups = conditions?.when ?? [];
  const setWhenGroups = (groups: SampleRuleConditionGroup[]) =>
    form.setValue("conditions.when", groups, { shouldDirty: true, shouldValidate: true });

  const matchedCount = rulesData?.items.find((r) => r.ruleId === ruleId)?.matchedCount ?? 0;
  const isDirty = form.formState.isDirty;

  const handleSave = async () => {
    if (!canSave) return;
    const values = form.getValues();
    const result =
      isEditing && ruleId
        ? await updateRule.mutateAsync({ ruleId, ...toUpdateRulePayload(values) })
        : await createRule.mutateAsync(toCreateRulePayload(values));
    if (result.applyJobId) {
      startTracking(result.applyJobId, "apply_sample_rule", null);
    }
    if (!isEditing) onCreated?.();
    onOpenChange(false);
  };

  const handleBack = onBack ?? (() => onOpenChange(false));

  const requestClose = () => {
    if (showDiscardConfirm) return;
    if (isDirty) {
      setShowDiscardConfirm(true);
    } else {
      onOpenChange(false);
    }
  };

  const handleBackRequest = () => {
    if (isDirty) {
      setShowDiscardConfirm(true);
    } else {
      handleBack();
    }
  };

  return (
    <SlideoutMenu
      isOpen={open}
      className={overlayClassName}
      onOpenChange={(next) => {
        if (!next) requestClose();
      }}
      isDismissable
    >
      <SlideoutMenu.Header onClose={requestClose}>
        <h2 className="text-lg font-semibold text-primary">{isEditing ? "Edit Rule" : "New Rule"}</h2>
      </SlideoutMenu.Header>

      {showDiscardConfirm ? (
        <>
          <SlideoutMenu.Content className="flex flex-col items-center gap-0 pt-16">
            <div className="flex w-full flex-col items-center gap-4 rounded-2xl border border-secondary bg-unbox-cream-50 px-8 pt-4 pb-8">
              <div className="flex size-16 items-center justify-center rounded-full">
                <AlertTriangle className="size-6 text-warning-500" />
              </div>
              <div className="flex flex-col items-center gap-2 text-center">
                <p className="text-xl font-semibold text-primary">Unsaved changes</p>
                <p className="w-[360px] text-md text-tertiary">
                  Your changes will be permanently lost if you don't save them.
                </p>
              </div>
            </div>
          </SlideoutMenu.Content>

          <SlideoutMenu.Footer className="flex flex-row items-center gap-6">
            <Button
              color="tertiary"
              className="flex-1"
              onClick={() => {
                setShowDiscardConfirm(false);
                handleBack();
              }}
            >
              Discard Changes
            </Button>
            <Button color="secondary" className="flex-1" onClick={() => setShowDiscardConfirm(false)}>
              Keep Editing
            </Button>
            <Button
              color="primary"
              className="w-[184px]"
              isDisabled={!canSave}
              isLoading={createRule.isPending || updateRule.isPending}
              onClick={handleSave}
            >
              Save Changes
            </Button>
          </SlideoutMenu.Footer>
        </>
      ) : (
        <>
          <SlideoutMenu.Content className="flex flex-col gap-6">
            <div className="flex flex-row items-end gap-4">
              <div className="flex-1">
                <Input
                  label="Rule Name"
                  placeholder="Name this rule"
                  value={name}
                  onChange={(value) => form.setValue("name", value, { shouldDirty: true, shouldValidate: true })}
                />
              </div>
              <Toggle
                isSelected={isActive}
                onChange={(checked) => form.setValue("isActive", checked, { shouldDirty: true })}
                label={isActive ? "Rule Enabled" : "Rule Disabled"}
              />
            </div>

            <div inert={!isActive} className={cx("flex flex-col gap-8", !isActive && "opacity-[0.58]")}>
              <div className="flex flex-col gap-3">
                <span className="text-xs font-semibold tracking-wide text-secondary uppercase">
                  When creator matches
                </span>
                {whenGroups.map((group, index) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: groups have no stable id, index is fine for a locally-edited draft
                  <div key={index} className="flex flex-col gap-2">
                    {index > 0 && (
                      <div className="flex items-center gap-2">
                        <div className="h-px flex-1 bg-secondary" />
                        <span className="text-xs font-semibold text-secondary">AND</span>
                        <div className="h-px flex-1 bg-secondary" />
                      </div>
                    )}
                    <ConditionGroupCard
                      group={group}
                      fieldOptions={FIELD_OPTIONS}
                      cohortOptions={cohortOptions}
                      onChange={(updated) => setWhenGroups(whenGroups.map((g, i) => (i === index ? updated : g)))}
                      onRemoveGroup={
                        whenGroups.length > 1
                          ? () => setWhenGroups(whenGroups.filter((_, i) => i !== index))
                          : undefined
                      }
                    />
                  </div>
                ))}
                <button
                  type="button"
                  className="flex items-center gap-1.5 rounded-xl border border-secondary bg-unbox-cream-50 p-3 text-sm font-medium text-secondary hover:text-primary hover:border-primary transition-colors"
                  onClick={() => setWhenGroups([...whenGroups, makeConditionGroup()])}
                >
                  <Plus className="size-4 text-quaternary" />
                  Add group logic
                </button>
              </div>

              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold tracking-wide text-secondary uppercase">Except when</span>
                  <div className="h-px flex-1 bg-border-secondary" />
                </div>
                {exceptGroup ? (
                  <ConditionGroupCard
                    group={exceptGroup}
                    fieldOptions={FIELD_OPTIONS}
                    cohortOptions={cohortOptions}
                    onChange={setExceptGroup}
                    onRemoveGroup={() => setExceptGroup(null)}
                    dashed
                  />
                ) : (
                  <button
                    type="button"
                    className="flex items-center justify-center gap-1.5 rounded-xl border border-dashed border-secondary bg-unbox-cream-50 p-3 text-sm font-medium text-secondary hover:text-primary hover:border-primary transition-colors"
                    onClick={() => setExceptGroup(makeConditionGroup())}
                  >
                    <Plus className="size-4 text-quaternary" />
                    Add Exception
                  </button>
                )}

                <div className="flex items-center justify-start gap-2 mt-4">
                  <span className="text-xs font-semibold text-secondary">OR</span>
                  <div className="h-px flex-1 bg-border-secondary" />
                </div>

                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-semibold tracking-wide text-secondary uppercase">
                    They're individually exempt
                  </span>
                  <Tooltip
                    title="These creators are always exempt, even if they match the rule's conditions."
                    placement="top"
                  >
                    <TooltipTrigger>
                      <HelpCircle className="size-3.5 text-quaternary cursor-help" />
                    </TooltipTrigger>
                  </Tooltip>
                </div>
                <ExemptCreatorList />
              </div>

              <div className="flex flex-col gap-3">
                <span className="text-xs font-semibold tracking-wide text-secondary uppercase">Then</span>
                <SampleRuleActionSelect
                  value={action}
                  onChange={(next) => form.setValue("action", next, { shouldDirty: true, shouldValidate: true })}
                  isDisabled={isEditing}
                />
              </div>
            </div>
          </SlideoutMenu.Content>

          <SlideoutMenu.Footer className="flex flex-col gap-3">
            {isActive ? (
              conditions && <ImpactPreview conditions={conditions as SampleRuleConditions} />
            ) : (
              <div className="flex flex-col gap-3 rounded-xl border border-warning-200 bg-warning-50 p-4">
                <p className="text-sm text-secondary">
                  From now on, pending sample requests won't be auto-{action === "ignore" ? "ignored" : "approved"} by
                  this rule.
                </p>
                <div className="h-px bg-warning-200" />
                <Checkbox
                  size="sm"
                  isSelected={matchedCount === 0 ? false : reprocessPendingRequests}
                  isDisabled={matchedCount === 0}
                  onChange={(checked) => form.setValue("reprocessPendingRequests", checked, { shouldDirty: true })}
                  label={
                    matchedCount === 0
                      ? "No pending requests currently match this rule."
                      : `Also re-review the ${matchedCount} pending requests already matched by this rule.`
                  }
                />
              </div>
            )}
            <div className="flex flex-row justify-between gap-3">
              <Button color="secondary" iconLeading={ArrowLeft} onClick={handleBackRequest}>
                Back
              </Button>
              <Button
                color="primary"
                isDisabled={!canSave}
                isLoading={createRule.isPending || updateRule.isPending}
                onClick={handleSave}
              >
                Save
              </Button>
            </div>
          </SlideoutMenu.Footer>
        </>
      )}
    </SlideoutMenu>
  );
}
